import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf.mjs";
import { loadPdfDocument } from "./utils";
import { compileCustomPatterns, type CustomPattern, type SafeMatcher } from "./safeRegex";

/** What put a mark there: a typed term, one of the built-in detectors, a custom pattern, or a form-field value. Used for the summary only. */
export type MarkKind = PatternKind | "text" | "custom" | "field";

/** Fractional (0-1) box relative to the displayed (upright) page. */
export interface RedactionBox { x: number; y: number; w: number; h: number }

/** A box that also remembers why it was marked. `group` is shared by the boxes of one match (a match across two lines makes two boxes). Hand-drawn boxes have neither. */
export interface MarkedBox extends RedactionBox { kind?: MarkKind; group?: number }

export interface SearchOptions {
  matchCase?: boolean;
  wholeWord?: boolean;
  /** Built-in patterns to mark in addition to the typed terms. */
  patterns?: PatternKind[];
  /** The person's own patterns (simple wildcards or a regular expression), matched by the safe engine in safeRegex.ts. */
  custom?: CustomPattern[];
}

/** A browser RegExp (typed terms and built-in detectors) or the safe engine (custom patterns). */
export type Matcher = RegExp | SafeMatcher;
const isSafe = (m: Matcher): m is SafeMatcher => (m as SafeMatcher).safe === true;
import { NAME_SOURCE } from "./nameDetect";

export type PatternKind = "email" | "phone" | "card" | "ssn" | "ein" | "name";

/** Every detector, in the order the tool lists them. */
export const ALL_PATTERN_KINDS: PatternKind[] = ["email", "phone", "card", "ssn", "ein", "name"];

/** The "select all detectors" shortcut: ticks every detector, or clears them all when every one is already ticked. */
export function toggleAllPatterns(current: PatternKind[]): PatternKind[] {
  return ALL_PATTERN_KINDS.every((k) => current.includes(k)) ? [] : [...ALL_PATTERN_KINDS];
}

export const PATTERN_SOURCES: Record<PatternKind, string> = {
  email: "[\\w.+-]+@[\\w-]+(?:\\.[\\w-]+)+",
  phone: "(?<![\\d])(?:\\+?\\d{1,3}[\\s.-]?)?(?:\\(\\d{3}\\)|\\d{3})[\\s.-]?\\d{3}[\\s.-]?\\d{4}(?![\\d])",
  card: "(?<![\\d])(?:\\d[ -]?){12,18}\\d(?![\\d])",
  ssn: "(?<![\\d])\\d{3}-\\d{2}-\\d{4}(?![\\d])",
  // employer ID: two digits, a hyphen, seven digits
  ein: "(?<![\\d-])\\d{2}-\\d{7}(?![\\d-])",
  // likely personal names (titles, labels, common first names); always review the marks
  name: NAME_SOURCE,
};

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** Every matcher with the kind it counts as in the summary, in the order typed terms, built-in detectors, custom patterns. */
export function buildLabelledMatchers(query: string, opts: SearchOptions = {}): { kind: MarkKind; matcher: Matcher }[] {
  const flags = "gu" + (opts.matchCase ? "" : "i");
  const out: { kind: MarkKind; matcher: Matcher }[] = [];
  for (const raw of query.split(/\r?\n/)) {
    const term = raw.trim();
    if (!term) continue;
    let src = term.split(/\s+/).map(escapeRe).join("\\s+");
    if (opts.wholeWord) src = `(?<![\\p{L}\\p{N}_])${src}(?![\\p{L}\\p{N}_])`;
    out.push({ kind: "text", matcher: new RegExp(src, flags) });
  }
  for (const k of opts.patterns ?? []) out.push({ kind: k, matcher: new RegExp(PATTERN_SOURCES[k], "gu") });
  if (opts.custom?.length) for (const m of compileCustomPatterns(opts.custom, { ignoreCase: !opts.matchCase })) out.push({ kind: "custom", matcher: m });
  return out;
}

/** One matcher per search term (one term per line), built-in detector and custom pattern; spaces in a term match any run of whitespace. */
export function buildMatchers(query: string, opts: SearchOptions = {}): Matcher[] {
  return buildLabelledMatchers(query, opts).map((l) => l.matcher);
}

function rawRanges(text: string, matcher: Matcher): [number, number][] {
  if (isSafe(matcher)) return matcher.findAll(text);
  const out: [number, number][] = [];
  matcher.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = matcher.exec(text))) {
    if (m[0].length === 0) { matcher.lastIndex++; continue; }
    out.push([m.index, m.index + m[0].length]);
  }
  return out;
}

/** When two kinds mark the same stretch of text it is counted once, under the more specific kind: the person's own pattern, then the exact-format detectors, then names, then typed text. */
const KIND_PRIORITY: MarkKind[] = ["custom", "ssn", "ein", "card", "email", "phone", "name", "text", "field"];
const better = (a: MarkKind, b: MarkKind) => (KIND_PRIORITY.indexOf(a) <= KIND_PRIORITY.indexOf(b) ? a : b);

/** All [start,end) ranges in `text` hit by any matcher, merged when overlapping, each with the kind it is counted under. */
export function findLabelledRanges(text: string, labelled: { kind: MarkKind; matcher: Matcher }[]): { start: number; end: number; kind: MarkKind }[] {
  const ranges: { start: number; end: number; kind: MarkKind }[] = [];
  for (const l of labelled) for (const [start, end] of rawRanges(text, l.matcher)) ranges.push({ start, end, kind: l.kind });
  ranges.sort((a, b) => a.start - b.start);
  const merged: { start: number; end: number; kind: MarkKind }[] = [];
  for (const r of ranges) {
    const last = merged[merged.length - 1];
    if (last && r.start <= last.end) { last.end = Math.max(last.end, r.end); last.kind = better(last.kind, r.kind); }
    else merged.push({ ...r });
  }
  return merged;
}

/** All [start,end) ranges in `text` hit by any matcher, merged when overlapping. */
export function findRanges(text: string, matchers: Matcher[]): [number, number][] {
  return findLabelledRanges(text, matchers.map((matcher) => ({ kind: "text" as MarkKind, matcher }))).map((r) => [r.start, r.end]);
}

export interface TextRun { str: string; hasEOL?: boolean; matrix: number[]; width: number; /** Width of a string in the run's font (any unit); used to place partial matches. */ measure?: (s: string) => number }

export interface Span { run: number; from: number; to: number }

/** Joins runs into one string (a space after each line end) and maps ranges back to per-run spans. */
export function joinRuns(runs: TextRun[]): { text: string; owner: { run: number; idx: number }[] } {
  let text = "";
  const owner: { run: number; idx: number }[] = [];
  runs.forEach((r, ri) => {
    for (let i = 0; i < r.str.length; i++) { text += r.str[i]; owner.push({ run: ri, idx: i }); }
    if (r.hasEOL) { text += " "; owner.push({ run: -1, idx: 0 }); }
  });
  return { text, owner };
}

export function spansForRange(owner: { run: number; idx: number }[], start: number, end: number): Span[] {
  const spans: Span[] = [];
  for (let i = start; i < end; i++) {
    const o = owner[i];
    if (!o || o.run < 0) continue;
    const last = spans[spans.length - 1];
    if (last && last.run === o.run && last.to === o.idx) last.to = o.idx + 1;
    else spans.push({ run: o.run, from: o.idx, to: o.idx + 1 });
  }
  return spans;
}

/**
 * Box (in viewport px, y down) for characters [from,to) of a run. `matrix` is the run's text matrix
 * already combined with the page viewport transform, so this works on turned and cropped pages.
 * The box reaches 25% of the font size below the baseline and 10% above the cap height so letter
 * tails (g, j, p, q, y) are covered, and a little to each side.
 */
export function spanBox(run: TextRun, from: number, to: number): { x0: number; y0: number; x1: number; y1: number } {
  const [a, b, c, d, e, f] = run.matrix;
  const adv = Math.hypot(a, b) || 1;
  const hgt = Math.hypot(c, d) || 10;
  const ux = a / adv, uy = b / adv; // along the text
  const vx = c / hgt, vy = d / hgt; // glyph "up", in viewport coordinates
  const len = Math.max(1, run.str.length);
  const w = run.width; // viewport px (scale 1 -> pt)
  let o0 = (w * from) / len, o1 = (w * to) / len;
  const tot = run.measure ? run.measure(run.str) : 0;
  if (run.measure && tot > 0) {
    o0 = (w * run.measure(run.str.slice(0, from))) / tot;
    o1 = (w * run.measure(run.str.slice(0, to))) / tot;
  }
  const padSide = Math.max(2, hgt * 0.1);
  const lo = -0.25 * hgt, hi = 1.1 * hgt;
  const pts: [number, number][] = [];
  for (const o of [o0 - padSide, o1 + padSide]) for (const h of [lo, hi]) pts.push([e + ux * o + vx * h, f + uy * o + vy * h]);
  const xs = pts.map((p) => p[0]), ys = pts.map((p) => p[1]);
  return { x0: Math.min(...xs), y0: Math.min(...ys), x1: Math.max(...xs), y1: Math.max(...ys) };
}

/** Union of boxes on the same line so one match becomes one box per line. */
export function toFractionBox(b: { x0: number; y0: number; x1: number; y1: number }, pw: number, ph: number): RedactionBox {
  const x0 = Math.max(0, b.x0), y0 = Math.max(0, b.y0), x1 = Math.min(pw, b.x1), y1 = Math.min(ph, b.y1);
  return { x: x0 / pw, y: y0 / ph, w: Math.max(0, x1 - x0) / pw, h: Math.max(0, y1 - y0) / ph };
}

export interface HiddenFinding { page: number; kind: "comment" | "link" | "form" | "bookmark"; text: string; box?: RedactionBox }

export interface FindResult {
  boxes: Record<number, MarkedBox[]>;
  /** Matches in places that are not part of the page picture: comments, link addresses, form values. */
  hidden: HiddenFinding[];
  /** Pages with no text layer at all (probably scans). */
  pagesWithoutText: number[];
}

export async function findTextInPdf(file: File, query: string, opts: SearchOptions = {}): Promise<FindResult> {
  return findTextInPdfDoc(await loadPdfDocument(file), query, opts);
}

/** The search itself, on an already open pdf.js document (so it can be tested without a browser). */
export async function findTextInPdfDoc(pdf: any, query: string, opts: SearchOptions = {}): Promise<FindResult> {
  const labelled = buildLabelledMatchers(query, opts);
  const matchers = labelled.map((l) => l.matcher);
  const result: FindResult = { boxes: {}, hidden: [], pagesWithoutText: [] };
  if (matchers.length === 0) return result;

  for (let i = 0; i < pdf.numPages; i++) {
    const page = await pdf.getPage(i + 1);
    const vp = page.getViewport({ scale: 1 });
    const content = await page.getTextContent();
    const runs: TextRun[] = [];
    const ctx = typeof document !== "undefined" ? document.createElement("canvas").getContext("2d") : null;
    for (const it of content.items) {
      if (!("str" in it) || !("transform" in it)) continue;
      const t = it as { str: string; transform: number[]; width: number; hasEOL?: boolean; fontName?: string };
      const family = (t.fontName && (content.styles as any)[t.fontName]?.fontFamily) || "sans-serif";
      const measure = ctx ? (str: string) => { ctx.font = `100px ${family}`; return ctx.measureText(str).width; } : undefined;
      runs.push({ measure, str: t.str, hasEOL: t.hasEOL, width: t.width * (Math.hypot(vp.transform[0], vp.transform[1]) || 1), matrix: pdfjsLib.Util.transform(vp.transform, t.transform) });
    }
    if (!runs.some((r) => r.str.trim())) result.pagesWithoutText.push(i);
    const { text, owner } = joinRuns(runs);
    const boxes: MarkedBox[] = [];
    let group = 0;
    for (const r of findLabelledRanges(text, labelled)) {
      group++;
      for (const sp of spansForRange(owner, r.start, r.end)) boxes.push({ ...toFractionBox(spanBox(runs[sp.run], sp.from, sp.to), vp.width, vp.height), kind: r.kind, group });
    }

    // Hidden places: form values, comments, link addresses.
    const annots = await page.getAnnotations();
    const hit = (s: unknown) => typeof s === "string" && s && findRanges(s, matchers).length > 0;
    for (const an of annots as any[]) {
      const T = vp.transform, ap = (x: number, y: number) => [T[0] * x + T[2] * y + T[4], T[1] * x + T[3] * y + T[5]];
      const p1 = ap(an.rect[0], an.rect[1]), p2 = ap(an.rect[2], an.rect[3]);
      const rect = [p1[0], p1[1], p2[0], p2[1]];
      const box = toFractionBox({ x0: Math.min(rect[0], rect[2]), y0: Math.min(rect[1], rect[3]), x1: Math.max(rect[0], rect[2]), y1: Math.max(rect[1], rect[3]) }, vp.width, vp.height);
      if (an.subtype === "Widget") {
        const v = Array.isArray(an.fieldValue) ? an.fieldValue.join(" ") : an.fieldValue;
        if (hit(v)) { boxes.push({ ...box, kind: "field", group: ++group }); result.hidden.push({ page: i, kind: "form", text: String(v), box }); }
      } else if (an.subtype === "Link") {
        const u = an.url ?? an.unsafeUrl;
        if (hit(u)) result.hidden.push({ page: i, kind: "link", text: String(u) });
      } else if (an.subtype !== "Popup") {
        const c = an.contentsObj?.str ?? an.contents;
        if (hit(c)) result.hidden.push({ page: i, kind: "comment", text: String(c) });
      }
    }
    if (boxes.length > 0) result.boxes[i] = boxes;
  }
  const outline = await pdf.getOutline().catch(() => null);
  const walk = (nodes: any[] | null | undefined) => {
    for (const n of nodes ?? []) {
      if (n.title && findRanges(String(n.title), matchers).length > 0) result.hidden.push({ page: -1, kind: "bookmark", text: String(n.title) });
      walk(n.items);
    }
  };
  walk(outline);
  return result;
}
